import { useEffect } from 'react'

type SeoOptions = {
  title: string
  description?: string
  canonical?: string
  noindex?: boolean
}

const BASE_TITLE = 'VICE — Virgin Islands Commercial Environment'
const SITE_ORIGIN = 'https://usvice.us'

function setMeta(name: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setMetaProp(property: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Per-page SEO updates. Small DOM manipulations keep us off react-helmet.
 */
export function useSeo({ title, description, canonical, noindex }: SeoOptions) {
  useEffect(() => {
    const fullTitle = title === BASE_TITLE ? title : `${title} | VICE`
    document.title = fullTitle

    if (description) {
      setMeta('description', description)
      setMetaProp('og:description', description)
      setMeta('twitter:description', description)
    }

    setMetaProp('og:title', fullTitle)
    setMeta('twitter:title', fullTitle)

    const href = canonical ?? `${SITE_ORIGIN}${window.location.pathname}`
    setCanonical(href)
    setMetaProp('og:url', href)

    setMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow')
  }, [title, description, canonical, noindex])
}
